import { GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { TRIP_FEE_AMOUNT_COLUMN } from "../common";
import { buildExportColumns } from "./columns/buildExportColumns";
import { buildParkColumns } from "./columns/buildParkColumns";
import { buildWalletColumns } from "./columns/buildWalletColumns";
import { useTransactionsUrlState } from "./useTransactionsUrlState";
import { CurbPassTransactionsTableProps } from "./types";
import { useCurbsuiteTransactionsData } from "./data/useCurbsuiteTransactionsData";
import { useBusStationTransactionsData } from "./data/useBusStationTransactionsData";
import { TransactionsGrid } from "./TransactionsGrid";

/**
 * Baseline for bus-station "park" accounts, where "timeRange" is hidden by business logic, not by
 * user action, so it must never count toward the hidden-columns indicator there. It's forced via
 * `defaultVisibilityModel` (below) plus `hideable: false` on the column itself (see
 * columns/buildParkColumns.tsx) — NOT via a controlled `columnVisibilityModel` prop on DataGridPro,
 * which would fight with the uncontrolled model the Columns panel and localStorage restore both rely
 * on: a fresh object literal passed as that prop re-fires MUI's controlled-prop sync effect on every
 * render (its dependency array compares by reference) and unconditionally replaces the entire
 * visibility model, silently discarding any other column the user hid.
 *
 * For every other account/variant combination, "timeRange" IS an ordinary user-toggleable column
 * (or absent from the grid entirely when variant !== "park", where it's harmless to include as
 * true) — see `defaultVisibilityModel` below, which adds `timeRange: true` back in whenever
 * `isBusStationPark` is false.
 */
const CURBPASS_TRANSACTIONS_DEFAULT_VISIBILITY: GridColumnVisibilityModel = {
  transactionIndex: true,
  transactionAssignedDate: true,
  transactionDate: true,
  timeRange: true,
  operator: true,
  busStationTransactionType: true,
  licensePlate: true,
  addressStreet: true,
  transactionType: true,
  amount: true,
  serviceFeeAmount: true,
  actions: true,
};

export const CurbPassTransactionsTable: React.FC<CurbPassTransactionsTableProps> = ({
  variant,
  isBusStation = false,
  isAirportTripCharges = false,
  showsServiceFee = false,
}) => {
  const isBusStationPark = isBusStation && variant === "park";
  const defaultVisibilityModel = React.useMemo<GridColumnVisibilityModel>(
    () =>
      isBusStationPark
        ? { ...CURBPASS_TRANSACTIONS_DEFAULT_VISIBILITY, timeRange: false }
        : { ...CURBPASS_TRANSACTIONS_DEFAULT_VISIBILITY, timeRange: true },
    [isBusStationPark],
  );

  // Both bus stations and airports bill named operators, so both get the Operator column.
  const showsOperatorBilling = isBusStation || isAirportTripCharges;

  // GSP's wallet tab is the month-close surface ("Monthly Charges"): which month was charged, to
  // which operator, for how much. Other wallet payees see reloads and refunds, where a charged
  // month is meaningless -- so this reshapes the tab for airports only.
  const isAirportWallet = isAirportTripCharges && variant === "wallet";

  // The billable event row differs by client: a park session for everyone else, a "trip_charge"
  // (one LPR entry capture) for airports. Everything downstream keys off this rather than
  // hardcoding "park".
  const parkTransactionType = isAirportTripCharges ? ("trip_charge" as const) : ("park" as const);

  const defaultSort = showsOperatorBilling ? "transactionDate+desc" : "transactionIndex+desc";
  const {
    page,
    handlePageChange,
    sortModel,
    handleSortModelChange,
    orderBy,
    appliedFilters,
    filterModel,
    handleApplyFilters,
    handleFilterModelChange,
    now,
    selectedDate,
    setSelectedDate,
    dateRangeFilter,
  } = useTransactionsUrlState({ defaultSort, isBusStation, variant });
  const [searchParams] = useSearchParams();

  const TABLE_COLUMNS = React.useMemo(
    () =>
      variant === "park"
        ? buildParkColumns({
            isBusStation,
            isAirportTripCharges,
            showsOperatorBilling,
            showsServiceFee,
            searchParams,
          })
        : buildWalletColumns({ isAirportWallet, showsOperatorBilling, showsServiceFee, searchParams }),
    [variant, searchParams, isBusStation, isAirportTripCharges, showsOperatorBilling, showsServiceFee, isAirportWallet],
  );

  const EXPORT_COLUMNS = React.useMemo(
    // Airports export exactly the on-screen columns plus the local trip time, per the field list
    // product specified for the monthly charging spreadsheet:
    // ID / Date / Time / Operator / Type / Plate / Address / Amount.
    // Both options describe a per-trip row, so they belong to the Transactions tab only. On the
    // Monthly Charges tab Time would read off the reconciled period start (00:00, or the epoch for
    // the rows with no period at all) and Vehicle Type would be an em dash in every cell.
    () => [
      ...buildExportColumns(TABLE_COLUMNS, {
        includeTripTime: isAirportTripCharges && variant === "park",
        vehicleTypeAfterOperator: isAirportTripCharges && variant === "park",
        isBusStation,
        // Every table but the airport Transactions tab can show adjustments (nothing can reference
        // a trip charge), and that tab's export field list is product-specified (AUT-6163).
        includeAdjustmentColumns: !(isAirportTripCharges && variant === "park"),
      }),
      ...(isBusStation ? [TRIP_FEE_AMOUNT_COLUMN] : []),
    ],
    [TABLE_COLUMNS, isBusStation, isAirportTripCharges, variant],
  );

  const curbsuiteData = useCurbsuiteTransactionsData({
    variant,
    parkTransactionType,
    isAirportWallet,
    appliedFilters,
    dateRangeFilter,
    orderBy,
    page,
    skip: isBusStationPark,
  });

  const busStationData = useBusStationTransactionsData({
    appliedFilters,
    dateRangeFilter,
    orderBy,
    page,
    skip: !isBusStationPark,
  });

  const { data, loading, error, fetchTotalCount, fetchDataBatch } = isBusStationPark ? busStationData : curbsuiteData;

  return (
    <TransactionsGrid
      // Scoped by variant/isBusStation, not just account — those drive different column sets.
      tableName={`curbpass-transactions-table-${variant}-${isBusStation}-${isAirportTripCharges}`}
      defaultVisibilityModel={defaultVisibilityModel}
      columns={TABLE_COLUMNS}
      exportColumns={EXPORT_COLUMNS}
      exportFileName={`curbpass_${variant}_${Date.now()}`}
      rows={data?.transactions || []}
      rowCount={data?.aggregate.aggregate.count || 0}
      loading={loading}
      error={error}
      page={page}
      onPageChange={handlePageChange}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      filterModel={filterModel}
      onFilterModelChange={handleFilterModelChange}
      onApplyFilters={handleApplyFilters}
      fetchTotalCount={fetchTotalCount}
      fetchDataBatch={fetchDataBatch}
      selectedDate={selectedDate}
      onChangeSelectedDate={setSelectedDate}
      maxDate={now}
    />
  );
};

export type { CurbPassTransactionsTableVariant, CurbPassTransactionsTableProps } from "./types";

export default CurbPassTransactionsTable;
